import React from "react";
import { FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from "@chakra-ui/react";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { FormStack } from "../components/FormStack/FormStack";
import { ChevronUpIcon } from "@chakra-ui/icons";

interface PushNotificationsConfigurationDetailFormValues {
  notificationTypesSettings: {
    generalNotificationEnabled: boolean;
  };
}

interface PushNotificationsConfigurationDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  configuration?: CherryHubAdminApi.PushNotificationsConfiguration;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess?: (
    configuration?: CherryHubAdminApi.PushNotificationsConfiguration
  ) => void;
  onFailure?: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  submitForm,
  isValid,
  onCancel,
  values,
  businessId,
}: PushNotificationsConfigurationDetailFormProps &
  FormikProps<PushNotificationsConfigurationDetailFormValues>) => {
  return (
    <Box width="100%" alignSelf="flex-start">
      <FormStack>
        <HStack w="100%" mb={4} justifyContent={"flex-end"}>
          <Box>
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
          </Box>

          <Box>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </Box>
        </HStack>

        <VStack w="100%" maxW="800px" alignItems="flex-start" spacing={4}>
          <Accordion
            defaultIndex={[0]}
            w="100%"
            allowToggle
            allowMultiple
            variant="section"
          >
            <AccordionItem>
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <b>Notification Settings</b>
                      </Box>
                      {isExpanded ? (
                        <ChevronUpIcon color="white" fontSize="xl" />
                      ) : (
                        <AccordionIcon />
                      )}
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <VStack w="100%" alignItems="flex-start" spacing={4}>
                      <SwitchField
                        horizontal
                        valueProps={{ flex: 2 }}
                        label="General Notifications"
                        name="notificationTypesSettings.generalNotificationEnabled"
                      />
                    </VStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </Accordion>
        </VStack>

        <HStack width="100%" justifyContent="start" spacing="3" pt="8">
          <Button
            colorScheme="cherryButton"
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting || !isValid}
            onClick={submitForm}
          >
            Save
          </Button>
          <Button disabled={isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </HStack>
      </FormStack>
    </Box>
  );
};

export const PushNotificationsConfigurationDetailForm = withFormik<
  PushNotificationsConfigurationDetailFormProps,
  PushNotificationsConfigurationDetailFormValues
>({
  mapPropsToValues: ({ configuration }) => {
    return {
      notificationTypesSettings: {
        generalNotificationEnabled:
          configuration?.NotificationTypeSettings?.GeneralNotificationEnabled ??
          false,
      },
    };
  },

  handleSubmit: async (values, { props, setSubmitting }) => {
    const configurationUpdate: CherryHubAdminApi.PushNotificationsConfigurationUpdate =
      {
        NotificationTypeSettings: {
          GeneralNotificationEnabled:
            values.notificationTypesSettings.generalNotificationEnabled,
        },
      };

    const result =
      await props.apiClient.createOrUpdatePushNotificationsConfiguration(
        props.businessId,
        configurationUpdate
      );

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }

    setSubmitting(false);
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,
})(InnerForm);
